import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { sdk } from '@/services/sdk';
import { Button } from '@/components/ui/button';

export const route = { path: '/productivity', layout: 'owner', access: "profile === 'Agent_c'" };
export const nav = { icon: 'ChartNoAxesCombined', label: 'Productivity', section: 'Performance', profiles: ['Agent_c'], order: 40 };

export default function Productivity() {
  const { data: jobs, loading: jobsLoading, error: jobsError, run: refreshJobs } = useFetch(() => sdk.table('jobs_c').select(['Name', 'status_c', 'scheduledAt_c']).orderByDesc('CreatedOn').limit(500, 0).fetch().then((r) => {
    if (!r.success) throw new Error(r.message || 'Could not load jobs');
    return r.data;
  }), []);
  const { data: events, loading: eventsLoading, error: eventsError, run: refreshEvents } = useFetch(() => sdk.table('tracking_events_c').select(['Name', 'jobReference_c', 'eventType_c', 'capturedAt_c']).orderByDesc('CreatedOn').limit(500, 0).fetch().then((r) => {
    if (!r.success) throw new Error(r.message || 'Could not load visit events');
    return r.data;
  }), []);
  const { data: reviews, loading: reviewsLoading, error: reviewsError, run: refreshReviews } = useFetch(() => sdk.table('reviews_c').select(['Name', 'jobReference_c', 'customerRating_c', 'followUpStatus_c']).orderByDesc('CreatedOn').limit(500, 0).fetch().then((r) => {
    if (!r.success) throw new Error(r.message || 'Could not load reviews');
    return r.data;
  }), []);
  const metrics = useMemo(() => {
    const jobRows = jobs ?? [];
    const eventRows = events ?? [];
    const completed = jobRows.filter((job) => job.status_c === 'Completed').length;
    const arrivals = eventRows.filter((event) => event.eventType_c === 'Arrived' && event.jobReference_c);
    const jobById = new Map(jobRows.map((job) => [String(job.Id), job]));
    const comparable = arrivals.map((event) => ({ event, job: jobById.get(String(event.jobReference_c)) })).filter(({ event, job }) => job?.scheduledAt_c && event.capturedAt_c);
    const onTime = comparable.filter(({ event, job }) => new Date(event.capturedAt_c).getTime() <= new Date(job.scheduledAt_c).getTime()).length;
    const ratings = (reviews ?? []).map((review) => Number(review.customerRating_c)).filter((rating) => Number.isFinite(rating) && rating >= 1 && rating <= 5);
    return {
      completed,
      onTime: comparable.length ? Math.round((onTime / comparable.length) * 100) + '%' : 'Not measured',
      arrivalSample: comparable.length,
      averageRating: ratings.length ? (ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length).toFixed(1) + ' / 5' : 'No reviews',
      reviewCount: ratings.length
    };
  }, [jobs, events, reviews]);
  const loading = jobsLoading || eventsLoading || reviewsLoading;
  const error = jobsError || eventsError || reviewsError;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Performance</p>
          <h1 className="mt-2 font-heading text-3xl font-bold">Productivity management</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Scorecards are calculated from saved job, visit and review records. No demo estimates are presented as real results.</p>
        </div>
        <Button variant="outline" onClick={() => { refreshJobs(); refreshEvents(); refreshReviews(); }} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh metrics'}</Button>
      </header>
      {error && <p className="rounded-xl border border-destructive/30 p-4 text-sm text-destructive">Some metrics could not be loaded: {error.message}</p>}
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label="Completed jobs" value={String(metrics.completed)} note="From saved job status" />
        <Metric label="On-time arrival" value={metrics.onTime} note={metrics.arrivalSample + ' scheduled arrival event(s) compared'} />
        <Metric label="First-time resolution" value="Not measured" note="Requires a verified rework / repeat-visit signal" />
        <Metric label="Customer rating" value={metrics.averageRating} note={metrics.reviewCount + ' rated review(s) included'} />
      </section>
      <section className="glass-panel rounded-[24px] p-6">
        <h2 className="font-heading text-xl font-bold">Fair-use notes</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">These are operational indicators, not automatic disciplinary decisions. Arrival rates depend on accurate scheduled times and recorded milestones; workers should be able to challenge incorrect events. Live GPS and attendance data are not collected by this page.</p>
        <Button asChild className="mt-5"><Link to="/worker-tracking">Record visit milestones</Link></Button>
      </section>
    </div>
  );
}

function Metric({ label, value, note }) {
  return (
    <div className="glass-panel rounded-[22px] p-5">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-3 font-heading text-2xl font-bold">{value}</p>
      <p className="mt-2 text-xs leading-5 text-muted-foreground">{note}</p>
    </div>
  );
}
